// ViewDocActivityRail — spec decision 7: "Activity timeline beside the
// document — sent / reminded / opened / page-opened / responded, from
// request_events. NOT the rendered email bodies."
//
// The distinction matters: this rail answers "what happened to this document",
// not "what did the email say". Newest first, because the operator's question
// is almost always about the most recent thing.
//
// TWO honesty rules run through this component.
//
// 1. FILL-ON-BEHALF ATTRIBUTION (§C5). There is no separate "recorded" event,
//    deliberately: a staff-typed completion IS a completion, and emitting two
//    events would imply two things happened. The attribution rides on the
//    completion event's `meta` instead, and when it is there this rail says so
//    — "Recorded by Miles on behalf of Michelle Garcia" — so an answer WHP
//    typed in is never mistaken for one the recipient typed.
//
// 2. SYNTHESIZED ENTRIES. Documents that predate the event log still show a
//    history, inferred from first-instant timestamps. Those are marked, because
//    an inferred event is not the same kind of fact as a recorded one, and a
//    timeline that hides the difference is worse than one with gaps.

import { formatWhen } from '@/lib/commandCenterClient';
import { OcEmpty } from '@/components/oc/bits';
import { attributionLine } from '@/lib/viewDocs';
import type { DocActivityEvent } from '@/lib/viewDocsClient';

/** Newest first, deterministic — timestamp descending, then original order. */
function newestFirst(events: DocActivityEvent[]): DocActivityEvent[] {
  return events
    .map((event, i) => ({ event, i }))
    .sort((a, b) => b.event.at.localeCompare(a.event.at) || a.i - b.i)
    .map((x) => x.event);
}

export function ViewDocActivityRail({
  activity,
  emptyNote = 'Nothing has happened to this document yet.',
}: {
  activity: DocActivityEvent[];
  emptyNote?: string;
}): React.JSX.Element {
  if (activity.length === 0) return <OcEmpty>{emptyNote}</OcEmpty>;

  const ordered = newestFirst(activity);
  const anySynthesized = ordered.some((e) => e.synthesized);

  return (
    <>
      <div className="vd-rail">
        {ordered.map((event, i) => {
          const attribution = attributionLine(event);
          return (
            <div
              key={`${event.at}-${i}`}
              className="vd-rail-item"
              data-type={event.type}
              data-synthesized={event.synthesized ? 'true' : undefined}
            >
              <span className="vd-rail-time">
                {formatWhen(event.at)}
                {event.synthesized && (
                  <span
                    className="vd-rail-inferred"
                    title="Inferred from the record's own timestamps — this document predates the event log."
                  >
                    inferred
                  </span>
                )}
              </span>
              <span className="vd-rail-text">{event.text}</span>
              {attribution && <span className="vd-rail-actor">{attribution}</span>}
              {!attribution && event.recipientName && (
                <span className="vd-rail-actor">{event.recipientName}</span>
              )}
            </div>
          );
        })}
      </div>
      {anySynthesized && (
        <div className="oc-meta" style={{ marginTop: 'var(--space-3)' }}>
          Entries marked <em>inferred</em> were worked out from the record's own
          timestamps — this document is older than the event log.
        </div>
      )}
    </>
  );
}
